import { FunctionComponent, memo, useContext } from "react";

import { IProps } from "./edit-tag.types";
import { EllipsisMenuContext, EllipsisMenuItem } from "@edgetier/client-components";
import { faEdit } from "@fortawesome/pro-solid-svg-icons";
import Modal, { ModalContent, ModalHeader, useModal } from "@edgetier/modal";
import { useQueryClient } from "react-query";
import { SpinnerUntil } from "@edgetier/components";
import { ITag, Route } from "@edgetier/watchtower-types";
import useTags from "hooks-for/tags/use-tags";
import TagForm from "components-for/tags/tag-form";
import { ITagFormValues } from "components-for/tags/tag-form/tag-form.types";
import { axios } from "utilities/axios";
import toast from "react-hot-toast";
import { getServerError } from "utilities/get-server-error";
import TagsListContext from "pages/tag-phrase-triggers/tags-list/tags-list-context/tags-list-context";
import "./edit-tag.scss";

/**
 * A button that triggers a modal containing a form used to edit the tag.
 * @param props.tagId The ID of the tag being edited.
 */
const EditTag: FunctionComponent<IProps> = ({ tagId }) => {
    const queryClient = useQueryClient();
    const { lockOpen, unlock } = useContext(EllipsisMenuContext);
    const { tagGroupType } = useContext(TagsListContext);

    const { closeModal, openModal, modalProps } = useModal({ onCloseModal: unlock });

    const { data: tag, isLoading } = useTags({}, { select: (tags) => tags.find((tag) => tag.tagId === tagId) });

    /**
     * Do a PATCH request to the backend to update the tag.
     * @param values The new values of the tag.
     */
    const onSubmit = async (values: ITagFormValues) => {
        try {
            const url = Route.Tag.replace(":tagId", tagId);
            await axios.patch(url, values);

            queryClient.setQueriesData<ITag[]>(Route.Tags, (tags) => {
                return (tags ?? []).map((tag) => (tag.tagId === values.tagId ? { ...tag, ...(values as ITag) } : tag));
            });

            toast.success(`"${values.tag}" has been edited successfully`);
            closeModal();
        } catch (serverError) {
            toast.error(getServerError(serverError));
        }
    };

    return (
        <>
            <EllipsisMenuItem
                closeAfterClick={false}
                icon={faEdit}
                onClick={() => {
                    lockOpen();
                    openModal();
                }}
            >
                Edit
            </EllipsisMenuItem>
            <Modal {...modalProps}>
                <ModalHeader>
                    <h2>Edit Tag</h2>
                </ModalHeader>
                <ModalContent>
                    <SpinnerUntil<ITag> data={tag} isReady={!isLoading && typeof tag !== "undefined"}>
                        {(data) => <TagForm initialValues={data} onSubmit={onSubmit} tagGroupType={tagGroupType} />}
                    </SpinnerUntil>
                </ModalContent>
            </Modal>
        </>
    );
};

export default memo(EditTag);
